import React, { ForwardedRef, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import useContactUsPhoneNo from "../../../hooks/use-contact-us-phone-no";
import useIsEH from "../../../hooks/use-is-eh";
import Portal from "../../Portal";
import Recommendation from "../../Recommendation";
import SendRequest from "../../SendRequestNeedHelp";
import HouseRule from "../../PropertyDetail/HouseRule";
import Amenities from "../../PropertyDetail/LohonoLayout/Amenities";
import PriceCalculatorDesktop from "../../PropertyDetail/LohonoLayout/PriceCalculator/Desktop";
import PriceCalculatorMobile from "../../PropertyDetail/LohonoLayout/PriceCalculator/Mobile";
import ThingsToDo from "../../PropertyDetail/LohonoLayout/ThingsToDo";
import useCalculatorFocus from "../../PropertyDetail/LohonoLayout/use-calculator-focus";
import { useDefaultPropertyConfig } from "../../../hooks/use-property-config";
import Modal from "../../../hooks/use-modal";
import RoomDetails from "../../PropertyDetail/LohonoLayout/RoomDetails";
//import useRoomDetails from "../../../hooks/use-room-details";
import { useClusterPriceCalculatorValue } from "../../../hooks/use-cluster-price-calculator";
import clxs from "./property-cluster-layout.module.css";
import useIsMobile from "../../../hooks/use-is-mobile";
import useMealPlanOptions from "../../../hooks/use-meal-plan-options";
import GuestReviews from "../../PropertyDetail/GuestReviews";
import ExclusiveCouponOffers from "../../ExclusiveCouponOffers";
import InVillaServices from "../../PropertyDetail/InVillaServices";
import ExclusiveCouponOffersMobile from "../../ExclusiveCouponOffersMobile";
import { useQueryParam, useQueryParamValue } from "../../../hooks/use-query-param";
import { useCouponSource } from "../../../hooks/use-price-calculator/use-coupon-source";
import BasicInfoDetail from "../BasicInfoDetail";
import ClusterBanner from "../ClusterBanner";
import ScrollableTabs from "../ScrollableTabs";
import RoomLayout from "../../PropertyDetail/LohonoLayout/RoomLayout";
import ClusterHouseRules from "../ClusterHouseRules";
import useClusterReviews from "../../../hooks/use-cluster-reviews";
import useClusterSimilar from "../../../hooks/use-cluster-similar";

interface PropertyClusterLayoutProps {
  priceCalculatorId: string;
  path: string;
  locationSlug: string;
  clusterDetails: ClusterProperty;
  onShowGallery: (selectedPropertyImages: ClusterPropertyImages) => void;
  onShare: () => Promise<boolean>;
  onSendRequest: (values: any) => void;
  onBook: (values: any) => void;
  clusterSlugKey: string;
  checkinDateKey: string;
  checkoutDateKey: string;
  adultCountKey: string;
  childCountKey: string;
  couponCodeKey: string;
  availLoyaltyPointsKey: string;
  seniorCitizenDescription: string;
  seniorCitizenFriendly: boolean;
  isInitialDetailsLoading:boolean;
  isShowPriceCalculator: boolean;
  coupons: Coupon[];
}

const PropertyClusterLayout = forwardRef(
  function PropertyClusterLayoutCore(
    props:PropertyClusterLayoutProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
  const {
    priceCalculatorId,
    path,
    locationSlug,
    clusterDetails,
    onShowGallery,
    onShare,
    onSendRequest,
    onBook,
    clusterSlugKey,
    checkinDateKey,
    checkoutDateKey,
    adultCountKey,
    childCountKey,
    couponCodeKey,
    isShowPriceCalculator,
    //seniorCitizenDescription,
    //seniorCitizenFriendly,
    //isInitialDetailsLoading,
    coupons,
  } = props,
    { isMobile } = useIsMobile(),
    ecx = isMobile ? (clxs.hideMobileDivider) : (clxs.divider),
    dcx = isMobile ? (clxs.mobileDivider) : (clxs.divider),
    ccx = isMobile ? (clxs.mobileDivider) : (clxs.hideDivider),
    checkinDate = useQueryParamValue("date", checkinDateKey),
    checkoutDate = useQueryParamValue("date", checkoutDateKey),
    adultCount = useQueryParamValue("integer", adultCountKey, 2),
    childCount = useQueryParamValue("integer", childCountKey),
    roomOptionsRef = useRef<HTMLDivElement>(null),
    bedroomDetailRef = useRef<HTMLDivElement>(null),
    mealRef = useRef<HTMLDivElement>(null),
    reviewRef = useRef<HTMLDivElement>(null),
    overviewRef =useRef<HTMLDivElement>(null),
    amenitiesRef = useRef<HTMLDivElement>(null),
    locationRef = useRef<HTMLDivElement>(null),
    servicesRef = useRef<HTMLDivElement>(null),
    cancellationPolicyRef = useRef<HTMLUListElement>(null),
    houseRulesRef = useRef<HTMLUListElement>(null),
    // showRoomDetailsModal = useShowModal(ROOM_DETAILS_MODAL_ID),
    // getRoomDetails = useRoomDetails(),
    // This is the minimum adult limit which will be set for price calculator. This is based on the adult count selected in the listing page.
    [minAdultLimit, setMinAdultLimit] = useState<number>(adultCount),
    [couponCode, setCouponCode] = useQueryParam("string", couponCodeKey),
    {couponSource, setCouponSource} = useCouponSource(),
    { slug: clusterSlug } = clusterDetails,
    { isEH } = useIsEH(locationSlug),
    {
      view_layout,
      name,
      address,
      accommodation,
      bath_count,
      pool_count,
      proximity,
      adult_count, // Max adult count
      child_count,  // Max child count
      //description,
      //featured_in,
      top_highlights,
      meal_options,
      images,
      amenities,
      bedrooms,
      bedroom_count,
      nearby_places,
      property_places,
      lat,
      lng,
      google_map_link,
      house_rules,
      cancellation_policy,
      upcoming,
      //builder,
      //images,
      configs,
      //youtube_video_id,
      ratings_count,
      average_ratings,
      active_invilla_services,
    } = clusterDetails,    
    {
      veg,
      non_veg,
    } = meal_options,
    { mealPlanOptions } = useMealPlanOptions(clusterSlug),
    offering_type_ids = useQueryParamValue("string", OFFERING_ID_KEY),
    { reviews } = useClusterReviews(clusterSlug,offering_type_ids),
    { similarProperties: clusterProperties} = useClusterSimilar(clusterSlug, isEH, adultCountKey, childCountKey, checkinDateKey, checkoutDateKey),
    //{ similarProperties } = usePropertySimilar(clusterSlug, isEH, adultCountKey, childCountKey, checkinDateKey, checkoutDateKey),
    highlights = useMemo(
      () => {
        return top_highlights.slice(0, 5);
      },
      [top_highlights],
    ),
    isVegOrNonVegAvailable = useMemo(() => {
      return !!((mealPlanOptions && (mealPlanOptions.veg.length || mealPlanOptions.non_veg.length || mealPlanOptions.veg_non_veg.length)));
    }, [veg, non_veg, mealPlanOptions]),
    safeViewLayout = useMemo(
      () => view_layout,
      [configs, view_layout],
    ),
    /** 
     * Retrieve property configuration values
     */
    // { configArgs } = usePropertyConfigValues(clusterSlug ? clusterSlug : "", configs),
    // { coupons } = useClusterCoupons(
    //   clusterSlug,
    //   checkinDate?.toISOString(),
    //   checkoutDate?.toISOString(),
    //   adultCount,
    //   childCount,
    //   offering_type_ids,
    // ),
    // selected_configs = configArgs.filter(each => each.required_rooms > 0),
    // isMultiConfig = useMemo(
    //   () => safeViewLayout === "multi_config",
    //   [safeViewLayout],
    // ),
    // hasWellnessPromotion = useMemo(
    //   () => (WELLNESS_PROMOTION_PROPERTIES.includes(propertyDetails.slug)),
    //   [locationSlug],
    // ),
    // isMaharajahHouse = useMemo(
    //   () => (propertyDetails.slug === "maharajah-house"),
    //   [locationSlug],
    // ),
    // showVideo = useMemo(
    //   () => Boolean(youtube_video_id).valueOf(),
    //   [youtube_video_id],
    // ),
    calculatorRef = useCalculatorFocus(),
    {  tel } = useContactUsPhoneNo(),
    { tentativePrice } = useClusterPriceCalculatorValue(priceCalculatorId),
    isUnavailable = useMemo(
      () => tentativePrice?.status === "unavailable",
      [tentativePrice],
    ),
    soldOutText = useMemo(() => {
      let soldOutText: string = "";
      if(tentativePrice?.sold_out && !isUnavailable){
        const soldOutVillaNames = tentativePrice.property_configs
          ?.filter(each => each.sold_out)
          .map(each => each.offering_type_name)
          .filter(Boolean); // Removes undefined/null names

        // Format sold out text based on how many villas are sold out.
        if (soldOutVillaNames?.length === 1) {
          soldOutText = `${soldOutVillaNames[0]} is sold out and no longer available for your selected dates.`;
        } else if (soldOutVillaNames?.length === 2) {
          soldOutText = `${soldOutVillaNames[0]} & ${soldOutVillaNames[1]} are sold out and no longer available for your selected dates.`;
        } else if (soldOutVillaNames?.length > 2) {
          soldOutText = `${soldOutVillaNames.slice(0, -1).join(", ")} & ${soldOutVillaNames[soldOutVillaNames.length - 1]} are sold out and no longer available for your selected dates.`;
        }
      }
      return soldOutText;
    }, [tentativePrice, isUnavailable]),
    // handleViewRoomDetails = useCallback(
    //   async (config: PropertyConfig) => {
    //     const roomDetails = await getRoomDetails(
    //       propertySlug,
    //       config.config_id,
    //     );

    //     if (!roomDetails) {
    //       return;
    //     }

    //     await showRoomDetailsModal({ roomDetails: roomDetails });
    //   },
    //   [showRoomDetailsModal, getRoomDetails],
    // ),
    onMobileChange = () => {
      roomOptionsRef.current?.scrollIntoView();
    },
    handleCouponCodeChange = (couponCode: string, source: "manual" | "list") => {
      setCouponSource(source);
      return setCouponCode(couponCode);
    },
    // handleAutoApplyCoupon = () => {
    //   if (!tentativePrice) {
    //     return false;
    //   }

    //   if (couponCode === "NONE") {
    //     return true;
    //   }

    //   handleCouponCodeChange(couponCode);

    //   return true;
    // };
    
    // Memoized value to store the coupon code from the tentativePrice breakdown
    response_coupon_code = useMemo(
      () => tentativePrice?.breakdown?.coupon_code,
      [tentativePrice],
    ),
    handleAutoApplyCoupon = () => {
      if (!tentativePrice) {
        return false;
      }

      if (couponCode === "NONE") {
        return true;
      }

      if (!response_coupon_code) {
        return true;
      }

      // If no coupon in URL or current coupon is from backend, use backend response
      if (!couponCode || !couponCode.length || couponSource === "backend") {
        setCouponSource("backend");
        setCouponCode(response_coupon_code);
      }

      // Don't auto-apply if user has manually selected a coupon
      if (couponSource === "manual" || couponSource === "list") {
        return true;
      }

      return true;
    };

    // useOnce(
    //   handleAutoApplyCoupon,
    //   [tentativePrice,response_coupon_code],
    // );

    useEffect(()=>{
      handleAutoApplyCoupon()},
      [tentativePrice,response_coupon_code],
    ); 

    useEffect(() => {
      setMinAdultLimit(adultCount)
    },[]);

  // useEffect(() => {
  //   setLoader(isInitialDetailsLoading)
  // }, [isInitialDetailsLoading]);
  const [tabsData, setTabsData] = useState<TabData[]>([]);

  useEffect(() => {
    const data: TabData[] = [];
  
    // Always present
    data.push({ label: "Overview", elementRef: overviewRef });
  
    // Conditionally added sections
    if (amenities.length) {
      data.push({ label: "Amenities", elementRef: amenitiesRef });
    }

    data.push({ label: "Bedrooms", elementRef: bedroomDetailRef });
  
    if (active_invilla_services?.length) {
      data.push({ label: "In-Villa services", elementRef: servicesRef });
    }
  
    if (nearby_places.length) {
      data.push({ label: "Nearby Places", elementRef: locationRef });
    }
  
    if (reviews.length) {
      data.push({ label: "Reviews", elementRef: reviewRef });
    }
  
    if (house_rules.length) {
      data.push({ label: "House Rules", elementRef: houseRulesRef });
    }
  
    if (cancellation_policy.length) {
      data.push({ label: "Cancellation Policy", elementRef: cancellationPolicyRef });
    }
  
    setTabsData(data);
  }, [reviews]);  

  useDefaultPropertyConfig(clusterSlug, safeViewLayout, configs);
  return (
    <>
      <div className={clxs.container}> 
         <ClusterBanner
           carouselId="cluster-banner"
           className={clxs.banner}
           propertyName={name}
           address={address}
           onShare={onShare}
           onViewPhotos={onShowGallery}
           clusterVillas={images}
         />
         <ScrollableTabs 
           tabsData={tabsData}
           className={clxs.scrollableTabs}
           headerOffset={isMobile ? 140 : 180}
         />
        <div className={clxs.basicInfoLayout}>
          <BasicInfoDetail
            ratingsCount={ratings_count}
            reviewRef={reviewRef}
            averageRatings={average_ratings}
            googleMapLink={google_map_link}
            address={address}
            upcoming={upcoming}
            villaName={name}
            className={clxs.titleContainer}
            viewLayout={safeViewLayout}
            accommodation={accommodation}
            bathCount={bath_count}
            bedroomCount={bedroom_count}
            configs={[]}
            poolCount={pool_count}
            ref={overviewRef}
            proximity={proximity}
          />
          {highlights.length ? 
          <hr
            className={dcx}
            data-active={true}
          /> : null }
          {highlights.length ? (
            <div className={clxs.highlightsContainer}>
              {highlights.map((each, key) =>
                <div
                  key={key}
                  className={clxs.highlights}
                >
                  <img
                    src={each.icon}
                    alt={each.name}
                    className={clxs.highlightsIcon}
                  />
                  <div className={clxs.highlightsTitle}>
                    {each.name}
                  </div>
                </div>,
              )}
            </div>
          ) : null
          }
        </div>
        <hr
          className={ecx}
          data-active={true}
        />

        {amenities.length ? 
        (<Amenities 
          amenities={amenities} 
          isCluster={true}
          ref={amenitiesRef}
         />) : null}

        <hr
          className={ecx}
          data-active={true}
        />

          <RoomLayout
            bedrooms={bedrooms}
            carouselId={BEDROOM_DETAIL_ID}
            className={clxs.bedroomDetails}
            titleClassName={clxs.bedroomDetailsTitle}
            containerClassName={clxs.bedroomCarousel}
            title="Bedroom Details"
            ref={bedroomDetailRef}
          /> 

        <hr
          className={dcx}
          data-active={true}
        /> 

        {active_invilla_services?.length ?
         (<InVillaServices
           ref={servicesRef}
           carouselId={IN_VILLAS_SERVICES_CAROUSEL_ID}
           activeInvillaServices={active_invilla_services}
           title="In-Villa services"
           subtitle="Request for curated services and experience and make your stay an unforgettable one!"
          />) 
         : null
        }

        {(isShowPriceCalculator || upcoming) && (
          //Apply sticky styling to the entire calculator container only if there's 0 or 1 coupon available.
          // When multiple coupons are present, the sticky behavior is shifted to the exclusive coupon offers section below.
          <div className={coupons.length <= 1  ?  `${clxs.calculatorContainer} ${clxs.stickyElement}`:clxs.calculatorContainer}>
            <div 
              ref={ref} 
              className={clxs.stickyContainer}
            >
              {upcoming ? (
                <SendRequest
                  className={clxs.sendRequest}
                  title="Enquire now"
                  interest="rental"
                />
              ) : (
                // Desktop Calculator Only Visible in case of multi config since we have not provided multi config options in price calcultor of mobile.
                // Once options availabel this will be commented for Mobile
                isShowPriceCalculator &&
                <>
                  <PriceCalculatorDesktop
                    viewLayout={safeViewLayout}
                    id={priceCalculatorId}
                    path={path}
                    couponCodeKey={couponCodeKey}
                    propertySlugKey={clusterSlugKey}
                    checkinDateKey={checkinDateKey}
                    checkoutDateKey={checkoutDateKey}
                    adultCountKey={adultCountKey}
                    childCountKey={childCountKey}
                    maxAdultCount={adult_count || 24}
                    maxChildCount={child_count || 8}
                    minAdultCount={minAdultLimit}
                    configs={configs}
                    className={clxs.calculatorDesktop}
                    onSendRequest={onSendRequest}
                    onSubmit={onBook}
                    ref={calculatorRef}
                    hideLoyaltyPointsTicker={isEH}
                    mealRef={mealRef}
                    isVegOrNonVegAvailable={isVegOrNonVegAvailable}
                    average_ratings={average_ratings}
                    reviews_count={ratings_count}
                    villa_name={name}
                    isClusterDetail={true}
                    clusterDetails={clusterDetails}
                    offeringTypeIdsKey={OFFERING_ID_KEY}
                    soldOutText={soldOutText}
                  />
                </>
              )}
            </div>
            {/* Show sticky behavior on the Exclusive Coupon Offers section only when there are multiple coupons.
            This prevents double-sticky UI issues when both sections are sticky. */}
            <div 
              className={coupons.length <= 1 ? "" : clxs.stickyElement}
            >
            <ExclusiveCouponOffers
              className={clxs.exclusiveCouponOffersContainer}
              value={couponCode}
              propertySlug={clusterSlug}
              checkinDate={checkinDate?.toISOString()}
              checkoutDate={checkoutDate?.toISOString()}
              selected_configs = {[]}
              onChange={handleCouponCodeChange}
              coupons={coupons}
              isClusterDetail={true}
              offering_type_ids={offering_type_ids}
              adultCount={adultCount ?? undefined}
              childCount={childCount ?? undefined}
            />
            </div>
          </div>
        )}
        {active_invilla_services?.length  ?
        <hr
          className={dcx}
          data-active={true}
        />: null}
        {nearby_places.length ? (
          <div>
            <hr
              className={ccx}
              data-active={false}
            />
            <ThingsToDo
              name={name}
              lat={lat}
              lng={lng}
              nearbyPlaces={nearby_places}
              propertyPlaces={property_places}
              ref={locationRef}
            />
          </div>
        ) : null}

         {reviews.length ? <div className={clxs.reviewContainer}>
          {/* Render a horizontal line for mobile devices */}
          <hr
            className={isMobile ? ccx : dcx}
            data-active={true}
          />
          <GuestReviews
            ref={reviewRef}
            reviews={reviews}
            className={clxs.guestReviews}
            average_ratings={average_ratings}
            ratings_count={ratings_count}
            title="Guest Reviews"
            isSticky={isMobile}
            configs={configs}
            isCluster={true}
          /> </div> : null}

        {house_rules.length ? (
          <div>
            <hr
              className={isMobile ? ccx : dcx}
              data-active={true}
            />
            <ClusterHouseRules
              title="House rules"
              houseRules={house_rules}
              isSticky={isMobile}
              ref={houseRulesRef}
              className={clxs.houseRule}
            />
          </div>
        ) : null}
        {cancellation_policy.length ? (
          <div className={clxs.cancellationPolicy}>
            <hr
              className={isMobile ? ccx : dcx}
              data-active={true}
            />
            <HouseRule
              maxVisible={isMobile ? 0 : 1}
              title="Cancellation policy"
              houseRules={cancellation_policy}
              isSticky={isMobile ? true : false}
              ref={cancellationPolicyRef}
              className={clxs.houseRule}
            />
          </div>
        ) : null}
        
        {/* {featured_in ? null : (<div>&nbsp;</div>)}
        {nearby_places.length ? null : (<div>&nbsp;</div>)}
        {house_rules.length ? null : (<div>&nbsp;</div>)}
        {cancellation_policy.length ? null : (<div>&nbsp;</div>)} */}
        {/* If there are no reviews, render nothing */}
     

        {clusterProperties.length ? 
        <hr
          className={dcx}
          data-active={true}
        /> : null }
        <Recommendation
          title="Similar Clusters"
          carouselId={SIMILAR_CAROUSEL_ID}
          checkinDateKey={checkinDateKey}
          checkoutDateKey={checkoutDateKey}
          adultCountKey={adultCountKey}
          childCountKey={childCountKey}
          className={clxs.similarProperties}
          titleClassName={clxs.similarPropertiesTitle}
          containerClassName={clxs.similarCarousel}
          tracking="property_detail_similar_cluster_clicked"
          isSimilarClusters= {true}
          simliarClusters={clusterProperties}
        />
      </div>

      {isMobile ? (
        <>
          {/* <Portal className={ tentativePrice?.sold_out ? clxs.clusterSoldOutHeight : clxs.clusterSoldOut }>
            <div className={clxs.clusterSoldOutContainer}>
              {soldOutText}
            </div>
          </Portal> */}
          <Portal className={ tentativePrice?.sold_out ? clxs.exclusiveCouponOffersMobileHeight : clxs.exclusiveCouponOffersMobile }>
            <ExclusiveCouponOffersMobile
              value={couponCode}
              propertySlug={clusterSlug}
              checkinDate={checkinDate?.toISOString()}
              checkoutDate={checkoutDate?.toISOString()}
              adultCount={adultCount ?? undefined}
              childCount={childCount ?? undefined}
              onChange={handleCouponCodeChange}
              configs={configs}
              selected_configs = {[]}
              couponCount={coupons.length}
              coupons={coupons}
              isClusterDetail={true}
              offering_type_ids={offering_type_ids}
            />
          </Portal>
          <Portal 
            className={clxs.calculatorMobile}
            // isCalculatorMobile= {true}
          >
            {upcoming ? (
              <div className={clxs.enquireContainer}>
                <button
                  className={clxs.enquireSecondary}
                  onClick={onSendRequest.bind(null, undefined)}
                >
                  Enquiry
                </button>
                <a
                  href={tel}
                  className={clxs.callUsSecondary}
                >
                  Call us
                </a>
              </div>
            ) : (
              <PriceCalculatorMobile
                viewLayout={safeViewLayout}
                id={priceCalculatorId}
                couponCodeKey={couponCodeKey}
                checkinDateKey={checkinDateKey}
                checkoutDateKey={checkoutDateKey}
                adultCountKey={adultCountKey}
                childCountKey={childCountKey}
                maxAdultCount={adult_count || 24}
                maxChildCount={child_count || 8}
                configs={configs}
                propertySlug={clusterSlug}
                mealRef={mealRef}
                isVegOrNonVegAvailable={isVegOrNonVegAvailable}
                onSendRequest={onSendRequest}
                onSubmit={onBook}
                onChange={onMobileChange}
                average_ratings={average_ratings}
                reviews_count={ratings_count}
                villa_name={name}
                isClusterDetail={true}
                clusterDetails={clusterDetails}
                offeringTypeIdsKey={OFFERING_ID_KEY}
                offering_type_ids={offering_type_ids}
              />
            )}
          </Portal>
        </>
      ) : null}

      <Modal id={ROOM_DETAILS_MODAL_ID}>
        <RoomDetails />
      </Modal>
    </>
  );
})

export default PropertyClusterLayout;

const SIMILAR_CAROUSEL_ID = "similar-properties";

const IN_VILLAS_SERVICES_CAROUSEL_ID = "in-villas-properties";

const ROOM_DETAILS_MODAL_ID = "room-details-modal";

const BEDROOM_DETAIL_ID = "bedroom-details";

const OFFERING_ID_KEY = "offering_type_ids";

export interface TabData {
  label: string; 
  elementRef: React.RefObject<HTMLElement>;
}
